// Publishing, rollback and discarding the draft (`06` §3.9, `SC-F604`,
// `SC-F607`–`SC-F610`). The three operations that turn the shared draft into a
// generation, or throw it away.
//
// **A publish is successful only after the control plane reads back a digest
// from the instance equal to the one it computed at render time** (`SC-F608`).
// Anything else — timeout, mismatch, error envelope, a writer job that exited
// non-zero — fails the job, leaves the generation in `publish_failed` and keeps
// the previous generation marked active. There is no partial success, and the UI
// must not invent one.
//
// Four confirmations this module makes explicit rather than optional, because
// each exists to stop the API being silent about a consequence:
//
//   - `allow_restart` — a restart-class publish replaces the task on a
//     single-replica instance. Without the flag the answer is `409
//     restart_required` carrying the blast-radius text and the query-loss
//     disclosure (`SC-F607`).
//   - `acknowledge_authors` — the draft is shared. When it holds objects last
//     edited by someone else, publishing without the flag is `409 conflict` with
//     `details.authors` (`06` §3.9.1).
//   - `expected_digest` — optimistic concurrency over the draft. If a colleague
//     saved in between, the publish is `409 conflict` rather than shipping
//     something the caller never reviewed. **Required** on discard, and required
//     on the retry after a failed publish (`OD-22`).
//   - `override_reason` / `confirm_instance_name` — the two blocks an owner may
//     proceed past need a typed justification, and the lockout block additionally
//     needs the instance's name typed out (`03` §6.5, §7.5).

import { getJSON, newIdempotencyKey, sendJSON } from "./http.js";
import type { ChangeClass, PublishPath } from "./generations.js";
import type { JobStarted } from "./jobs.js";

const base = (instanceId: string) => `/v1/instances/${encodeURIComponent(instanceId)}`;

/**
 * PublishInput is the body of `POST /v1/instances/{id}/generations`
 * (`publish.Request`).
 *
 * Every field is optional on the wire and each absence has one meaning. The two
 * booleans are **acknowledgements** rather than options: absent means "I have not
 * been told about this", which is why their absence is a refusal carrying the
 * thing the caller has not been told rather than a default.
 */
export interface PublishInput {
  /** The change message. It is the only human record of *why* a generation
   * exists, and it appears in the history next to the author. */
  message?: string;
  /** The `rendered_digest` of the draft the operator actually reviewed. Optional
   * in general, **required** on a retry after a failed publish. */
  expected_digest?: string;
  /** Required when the draft's verdict restarts the instance. */
  allow_restart?: boolean;
  /** Required when the draft contains objects last edited by another account. */
  acknowledge_authors?: boolean;
  /** Turns the test gate on for this request. Absent means "use the
   * organization's setting"; `false` means "explicitly not for this publish" — a
   * distinction `OD-20` needs, because an organization that opted in must not be
   * able to opt out by omission. */
  require_tests?: boolean;
  /** Required and non-empty whenever the publish proceeds past an overridable
   * block. An empty value is the statement that no override was exercised, so it
   * is never defaulted to a placeholder. */
  override_reason?: string;
  /** `03` §7.5's hard confirmation for the lockout case: the caller types the
   * instance's name. It is separate from `override_reason` because the two answer
   * different questions — "why" and "yes, this instance". */
  confirm_instance_name?: string;
}

/** PublishReload records when the reload was triggered and when the digest was
 * confirmed. On the restart path `triggered` is when the rollout was issued. */
export interface PublishReload {
  triggered: string;
  confirmed: string;
}

/**
 * PublishResult is the `config.publish` job's result — `SC-F608`'s confirmation
 * contract (`publish.Outcome`).
 *
 * `rendered_digest === observed_digest` is what makes the publish true; show
 * both, because showing only one hides the check.
 */
export interface PublishResult {
  generation: number;
  rendered_digest: string;
  observed_digest: string;
  path: PublishPath | string;
  reload: PublishReload;
  /**
   * Measured unavailability in milliseconds, and the three values it can take are
   * three different statements. **0** is the hot-reload path, where it is exact
   * rather than optimistic: no task is replaced. **A number** on the restart path
   * is the rollout's own measurement of the drain and start phases. **null** is a
   * restart-class publish whose rollout did not report one — and it stays null
   * rather than being filled in from the wall clock, because that interval also
   * contains readiness polling and would overstate the outage. Render null as "not
   * measured", never as "none".
   */
  downtime_ms: number | null;
  /** The relative readiness gate's two snapshots, reported on success as well as
   * failure so the UI can say, truthfully, "published — catalog `legacy-dw` was
   * already unhealthy before this change and still is". */
  unhealthy_before?: string[];
  unhealthy_after?: string[];
}

/**
 * publish renders the draft, ships it and confirms it (`SC-F604`) → job. Watch
 * the returned job with `watchJob`; the digest read-back is one of its steps, so
 * the progress bar is meaningful rather than decorative.
 *
 *   const draft = await getDraft(id);
 *   const started = await publish(id, {
 *     message,
 *     expected_digest: draft.rendered_digest,
 *     allow_restart: draft.verdict.restarts,
 *     acknowledge_authors: hasOthersWork,
 *   });
 *   const done = await watchJob<PublishResult>(started.job.id);
 *
 * The `Idempotency-Key` is **required** by the route: a naive retry after a
 * timeout must not publish twice, and a replay returns the original job rather
 * than a second one.
 */
export function publish(
  instanceId: string,
  input: PublishInput,
  opts: { verification?: string } = {},
): Promise<JobStarted<PublishResult>> {
  return sendJSON<JobStarted<PublishResult>>(`${base(instanceId)}/generations`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
    verification: opts.verification,
  });
}

/** RollbackInput is the body of `POST .../generations/{n}/rollback`. It carries no
 * `expected_digest`: the content it publishes is a generation the instance already
 * confirmed once, the target is in the route's own path, and the restored draft
 * does not exist until the rollback creates it. */
export interface RollbackInput {
  message?: string;
  /** Required when rolling forward to that generation crosses a restart
   * boundary — a rollback is classified like any other publish, not applied in
   * place. */
  allow_restart?: boolean;
  override_reason?: string;
  confirm_instance_name?: string;
}

/**
 * rollback rolls **forward** to an older desired state (`SC-F610`) → job.
 *
 * The wording is not pedantry. Swarm retains exactly one previous service spec,
 * so "roll back two versions" is not a primitive anywhere in the stack: this
 * publishes a NEW generation whose content equals generation `n`, which is what
 * keeps the history append-only and the audit trail free of time moving
 * backwards. It is also why config GC has to be refcounted and delayed.
 *
 * A rollback that is policy-only relative to the live generation takes the reload
 * path with no restart, and the confirmation must say so; one that crosses a
 * `server.yaml`, secret, datasource, size or image boundary is restart-class and
 * states its gap.
 */
export function rollback(
  instanceId: string,
  n: number,
  input: RollbackInput = {},
): Promise<JobStarted<PublishResult>> {
  return sendJSON<JobStarted<PublishResult>>(
    `${base(instanceId)}/generations/${n}/rollback`,
    "POST",
    input,
    { idempotencyKey: newIdempotencyKey() },
  );
}

/** DiscardInput is the body of `POST .../draft/discard`. */
export interface DiscardInput {
  /** **Required.** A body without it answers `409 conflict` carrying the current
   * digest and the author roll-up — the two-step shape exists so the destructive
   * path cannot be reached by a client that never rendered the author list. */
  expected_digest: string;
  /** Required when the draft contains objects last edited by another account;
   * without it the answer is `403 forbidden` with `details.authors` naming them. */
  discard_others?: boolean;
}

/**
 * discardDraft reverts the draft to the live generation. Destructive and
 * irreversible: the draft is not versioned, so discarded work is gone.
 *
 * Render the author roll-up from `getDraft` **before** offering this, and pass
 * `discard_others` only after the operator has seen whose work they are about to
 * destroy. The route answers `204`.
 */
export function discardDraft(instanceId: string, input: DiscardInput): Promise<void> {
  return sendJSON<void>(`${base(instanceId)}/draft/discard`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * restartRequired reads a stored change class as the boolean the publish dialog
 * needs, for the one case where a verdict is not at hand — a generation history
 * row. Where there IS a verdict, read `verdict.restarts`: it is the server's own
 * answer, and a client that re-derives it from a string can disagree with the
 * refusal it is about to receive.
 */
export function restartRequired(changeClass: ChangeClass | string): boolean {
  return (
    changeClass === "datasource" ||
    changeClass === "server_config" ||
    changeClass === "secret_rotation" ||
    changeClass === "image_or_size"
  );
}

// ─────────────────────────────────────────── drift detection (`SC-F609`) ──

/** DriftGranularity says how precisely drift can be located.
 *
 * On this build it is always `digest_only`, and the distinction is load-bearing
 * rather than pedantic: "something drifted" and "this object drifted" are
 * different claims, and only the first is supportable. `object_spec` exists in
 * the vocabulary so a client branches on a value rather than on a boolean it has
 * to interpret; it is never returned today. */
export type DriftGranularity = "digest_only" | "object_spec";

/** DriftState is the verdict.
 *
 * `undetermined` is deliberately not folded into `in_sync`. "We compared and found
 * no difference" and "we could not compare" are different facts, and the second is
 * the state an instance sits in for the first seconds of every task start, for
 * every instance that has never published, and for every one whose last publish
 * failed. */
export type DriftState = "in_sync" | "drifted" | "undetermined" | "unavailable";

/** DriftClass names one of `02` §9.4's reconciler classes bearing on
 * configuration, with whether this route can see it.
 *
 * Two rows: `D4` — the instance is serving the wrong policy snapshot, which this
 * route measures — and `D3` — the live service spec is not what desired state
 * implies, which is a different question with a different repair and is observable
 * only from the process holding Docker access. A clean verdict here says nothing
 * about `D3`, and the UI has to say so. */
export interface DriftClass {
  code: string;
  class: string;
  severity: number;
  repair: string;
  observable: boolean;
  note: string;
}

/** DriftGeneration is the baseline drift is measured against.
 *
 * Both digests are carried because `SC-S608` requires it: "we sent X, it reports
 * Y" is diagnosable and "there is drift" is not. They are equal by construction —
 * a publish is only recorded active when the instance confirms the rendered digest
 * — and a report where they differ is our own bookkeeping inconsistency rather
 * than drift on the instance. */
export interface DriftGeneration {
  number: number;
  state: string;
  rendered_digest: string;
  confirmed_digest?: string;
  confirmed_at: string | null;
}

/** DriftReport is `GET /v1/instances/{id}/drift` (`SC-F609`).
 *
 * **Drift is defined against a CONFIRMED generation.** A generation is live only
 * after the control plane read `config_digest` back from the instance and found it
 * equal to what it rendered (`SC-F608`), so an instance with no confirmed
 * generation is `undetermined` and never "in sync" — reporting no drift for it
 * would be an assertion about a comparison nobody made.
 *
 * **The comparison is digest-only and the report says so on every response.**
 * `SLUICE-U08` Ask A would add a per-object `spec_sha256` to `GET /admin/policies`
 * and let the drifted object be named with no disclosure of content. It does not
 * exist at the pinned Sluice commit, so what ships detects *that* something
 * drifted and never *what*. The remedy is the same either way — republish the live
 * generation, because the control plane's generation history is the source of truth
 * — which is why the missing capability costs diagnosis rather than recovery. */
export interface DriftReport {
  instance: string;
  state: DriftState;
  /** A machine token for the `undetermined` and `unavailable` states:
   * `never_published`, `not_confirmed`, `no_snapshot_reported`, `not_serving`,
   * `unreachable` or `control_plane_inconsistency`. */
  reason?: string;
  detail: string;
  granularity: DriftGranularity;
  /** The sentence a client MUST render beside the verdict. */
  granularity_note: string;
  upstream: string;
  upstream_note: string;
  generation: DriftGeneration | null;
  observed: { config_digest: string; config_version: number; degraded: boolean } | null;
  /** An open draft, reported separately because an unpublished draft is **not**
   * drift: the instance is correctly running what was published. This is the
   * single most common reason a customer believes otherwise. */
  draft: { number: number; note: string } | null;
  classes: DriftClass[];
  remedy?: string;
  /** The reconciler's own rate limit on the automatic repair, so drift that
   * persists for minutes reads as the guard working rather than as a stuck pass. */
  repair_guard?: string;
  observed_at: string;
}

/** getDrift asks whether the instance is running what we published. */
export function getDrift(instanceId: string, signal?: AbortSignal): Promise<DriftReport> {
  return getJSON<DriftReport>(`${base(instanceId)}/drift`, { signal });
}
